import ExcelJS from "exceljs";
import type {
  LiquidityRatios,
  LeverageRatios,
  ProfitabilityRatios,
  EfficiencyRatios,
} from "@shared/financial-types";

interface BalanceSheetData {
  assets: {
    current: {
      cash: number;
      receivables: number;
      inventory: number;
      other: number;
      total: number;
    };
    fixed: {
      tangible: number;
      intangible: number;
      financial: number;
      total: number;
    };
    total: number;
  };
  liabilities: {
    current: {
      payables: number;
      shortTermDebt: number;
      accruals: number;
      other: number;
      total: number;
    };
    longTerm: {
      longTermDebt: number;
      provisions: number;
      other: number;
      total: number;
    };
    total: number;
  };
  equity: {
    capital: number;
    reserves: number;
    retainedEarnings: number;
    currentYearProfit: number;
    total: number;
  };
}

interface ProfitLossData {
  period: { startDate: number; endDate: number };
  revenue: { sales: number; services: number; other: number; total: number };
  expenses: {
    materials: number;
    personnel: number;
    operating: number;
    depreciation: number;
    financial: number;
  };
  result: {
    grossProfit: number;
    operatingProfit: number;
    netProfit: number;
  };
}

interface RatiosData {
  liquidity: LiquidityRatios;
  leverage: LeverageRatios;
  profitability: ProfitabilityRatios;
  efficiency: EfficiencyRatios;
  period: { startDate: number; endDate: number };
}

export async function exportBalanceSheetExcel(
  data: BalanceSheetData,
  companyName: string,
  asOfDate: number
) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Bilanz");

  // Set column widths
  worksheet.columns = [{ width: 40 }, { width: 20 }];

  // Header
  worksheet.mergeCells("A1:B1");
  const titleCell = worksheet.getCell("A1");
  titleCell.value = "BILANZ / BALANCE SHEET";
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: "center" };

  worksheet.mergeCells("A2:B2");
  const companyCell = worksheet.getCell("A2");
  companyCell.value = companyName;
  companyCell.font = { size: 12 };
  companyCell.alignment = { horizontal: "center" };

  worksheet.mergeCells("A3:B3");
  const dateCell = worksheet.getCell("A3");
  dateCell.value = `Per / As of: ${new Date(asOfDate).toLocaleDateString("de-CH")}`;
  dateCell.alignment = { horizontal: "center" };

  let row = 5;

  // Assets
  worksheet.getCell(`A${row}`).value = "AKTIVEN / ASSETS";
  worksheet.getCell(`A${row}`).font = { bold: true, size: 12 };
  row++;

  // Current Assets
  worksheet.getCell(`A${row}`).value = "Umlaufvermögen / Current Assets";
  worksheet.getCell(`A${row}`).font = { bold: true };
  worksheet.getCell(`B${row}`).value = data.assets.current.total / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "  Flüssige Mittel / Cash";
  worksheet.getCell(`B${row}`).value = data.assets.current.cash / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Forderungen / Receivables";
  worksheet.getCell(`B${row}`).value = data.assets.current.receivables / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Vorräte / Inventory";
  worksheet.getCell(`B${row}`).value = data.assets.current.inventory / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Übrige / Other";
  worksheet.getCell(`B${row}`).value = data.assets.current.other / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  // Fixed Assets
  row++;
  worksheet.getCell(`A${row}`).value = "Anlagevermögen / Fixed Assets";
  worksheet.getCell(`A${row}`).font = { bold: true };
  worksheet.getCell(`B${row}`).value = data.assets.fixed.total / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "  Sachanlagen / Tangible Assets";
  worksheet.getCell(`B${row}`).value = data.assets.fixed.tangible / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Immaterielle Anlagen / Intangible";
  worksheet.getCell(`B${row}`).value = data.assets.fixed.intangible / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Finanzanlagen / Financial Assets";
  worksheet.getCell(`B${row}`).value = data.assets.fixed.financial / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  // Total Assets
  row++;
  worksheet.getCell(`A${row}`).value = "TOTAL AKTIVEN / TOTAL ASSETS";
  worksheet.getCell(`A${row}`).font = { bold: true, size: 11 };
  worksheet.getCell(`B${row}`).value = data.assets.total / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  worksheet.getRow(row).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };
  row += 2;

  // Liabilities & Equity
  worksheet.getCell(`A${row}`).value = "PASSIVEN / LIABILITIES & EQUITY";
  worksheet.getCell(`A${row}`).font = { bold: true, size: 12 };
  row++;

  // Current Liabilities
  worksheet.getCell(`A${row}`).value =
    "Kurzfristiges Fremdkapital / Current Liabilities";
  worksheet.getCell(`A${row}`).font = { bold: true };
  worksheet.getCell(`B${row}`).value = data.liabilities.current.total / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "  Verbindlichkeiten / Payables";
  worksheet.getCell(`B${row}`).value = data.liabilities.current.payables / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value =
    "  Kurzfristige Schulden / Short-term Debt";
  worksheet.getCell(`B${row}`).value =
    data.liabilities.current.shortTermDebt / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Rückstellungen / Accruals";
  worksheet.getCell(`B${row}`).value = data.liabilities.current.accruals / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  // Long-term Liabilities
  row++;
  worksheet.getCell(`A${row}`).value =
    "Langfristiges Fremdkapital / Long-term Liabilities";
  worksheet.getCell(`A${row}`).font = { bold: true };
  worksheet.getCell(`B${row}`).value = data.liabilities.longTerm.total / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value =
    "  Langfristige Schulden / Long-term Debt";
  worksheet.getCell(`B${row}`).value =
    data.liabilities.longTerm.longTermDebt / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Rückstellungen / Provisions";
  worksheet.getCell(`B${row}`).value =
    data.liabilities.longTerm.provisions / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  // Equity
  row++;
  worksheet.getCell(`A${row}`).value = "Eigenkapital / Equity";
  worksheet.getCell(`A${row}`).font = { bold: true };
  worksheet.getCell(`B${row}`).value = data.equity.total / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "  Kapital / Capital";
  worksheet.getCell(`B${row}`).value = data.equity.capital / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Reserven / Reserves";
  worksheet.getCell(`B${row}`).value = data.equity.reserves / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Gewinnvortrag / Retained Earnings";
  worksheet.getCell(`B${row}`).value = data.equity.retainedEarnings / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "  Jahresgewinn / Current Year Profit";
  worksheet.getCell(`B${row}`).value = data.equity.currentYearProfit / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  // Total Liabilities & Equity
  row++;
  worksheet.getCell(`A${row}`).value =
    "TOTAL PASSIVEN / TOTAL LIABILITIES & EQUITY";
  worksheet.getCell(`A${row}`).font = { bold: true, size: 11 };
  worksheet.getCell(`B${row}`).value =
    (data.liabilities.total + data.equity.total) / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  worksheet.getRow(row).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };

  // Download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Bilanz_${companyName}_${new Date(asOfDate).toISOString().split("T")[0]}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function exportProfitLossExcel(
  data: ProfitLossData,
  companyName: string
) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Erfolgsrechnung");

  // Set column widths
  worksheet.columns = [{ width: 40 }, { width: 20 }];

  // Header
  worksheet.mergeCells("A1:B1");
  const titleCell = worksheet.getCell("A1");
  titleCell.value = "ERFOLGSRECHNUNG / PROFIT & LOSS";
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: "center" };

  worksheet.mergeCells("A2:B2");
  const companyCell = worksheet.getCell("A2");
  companyCell.value = companyName;
  companyCell.font = { size: 12 };
  companyCell.alignment = { horizontal: "center" };

  worksheet.mergeCells("A3:B3");
  const dateCell = worksheet.getCell("A3");
  const startDate = new Date(data.period.startDate).toLocaleDateString("de-CH");
  const endDate = new Date(data.period.endDate).toLocaleDateString("de-CH");
  dateCell.value = `Periode / Period: ${startDate} - ${endDate}`;
  dateCell.alignment = { horizontal: "center" };

  let row = 5;

  // Revenue
  worksheet.getCell(`A${row}`).value = "ERTRAG / REVENUE";
  worksheet.getCell(`A${row}`).font = { bold: true, size: 12 };
  row++;

  worksheet.getCell(`A${row}`).value = "Warenertrag / Sales Revenue";
  worksheet.getCell(`B${row}`).value = data.revenue.sales / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value =
    "Dienstleistungsertrag / Service Revenue";
  worksheet.getCell(`B${row}`).value = data.revenue.services / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Übrige Erträge / Other Revenue";
  worksheet.getCell(`B${row}`).value = data.revenue.other / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Gesamtertrag / Total Revenue";
  worksheet.getCell(`A${row}`).font = { bold: true };
  worksheet.getCell(`B${row}`).value = data.revenue.total / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  worksheet.getRow(row).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };
  row += 2;

  // Expenses
  worksheet.getCell(`A${row}`).value = "AUFWAND / EXPENSES";
  worksheet.getCell(`A${row}`).font = { bold: true, size: 12 };
  row++;

  worksheet.getCell(`A${row}`).value = "Materialaufwand / Material Expenses";
  worksheet.getCell(`B${row}`).value = data.expenses.materials / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Personalaufwand / Personnel Expenses";
  worksheet.getCell(`B${row}`).value = data.expenses.personnel / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Betriebsaufwand / Operating Expenses";
  worksheet.getCell(`B${row}`).value = data.expenses.operating / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Abschreibungen / Depreciation";
  worksheet.getCell(`B${row}`).value = data.expenses.depreciation / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Finanzaufwand / Financial Expenses";
  worksheet.getCell(`B${row}`).value = data.expenses.financial / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  row += 2;

  // Results
  worksheet.getCell(`A${row}`).value = "Bruttogewinn / Gross Profit";
  worksheet.getCell(`A${row}`).font = { bold: true };
  worksheet.getCell(`B${row}`).value = data.result.grossProfit / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "Betriebsgewinn / Operating Profit";
  worksheet.getCell(`A${row}`).font = { bold: true };
  worksheet.getCell(`B${row}`).value = data.result.operatingProfit / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "JAHRESGEWINN / NET PROFIT";
  worksheet.getCell(`A${row}`).font = { bold: true, size: 11 };
  worksheet.getCell(`B${row}`).value = data.result.netProfit / 100;
  worksheet.getCell(`B${row}`).numFmt = "CHF #,##0.00";
  worksheet.getCell(`B${row}`).font = { bold: true };
  worksheet.getRow(row).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFE0E0E0" },
  };

  // Download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Erfolgsrechnung_${companyName}_${new Date(data.period.endDate).toISOString().split("T")[0]}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}

export async function exportRatiosExcel(data: RatiosData, companyName: string) {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Kennzahlen");

  // Set column widths
  worksheet.columns = [{ width: 40 }, { width: 15 }, { width: 15 }];

  // Header
  worksheet.mergeCells("A1:C1");
  const titleCell = worksheet.getCell("A1");
  titleCell.value = "KENNZAHLEN / FINANCIAL RATIOS";
  titleCell.font = { bold: true, size: 16 };
  titleCell.alignment = { horizontal: "center" };

  worksheet.mergeCells("A2:C2");
  const companyCell = worksheet.getCell("A2");
  companyCell.value = companyName;
  companyCell.font = { size: 12 };
  companyCell.alignment = { horizontal: "center" };

  worksheet.mergeCells("A3:C3");
  const dateCell = worksheet.getCell("A3");
  const startDate = new Date(data.period.startDate).toLocaleDateString("de-CH");
  const endDate = new Date(data.period.endDate).toLocaleDateString("de-CH");
  dateCell.value = `Periode / Period: ${startDate} - ${endDate}`;
  dateCell.alignment = { horizontal: "center" };

  let row = 5;

  // Column headers
  worksheet.getCell(`A${row}`).value = "Kennzahl / Ratio";
  worksheet.getCell(`B${row}`).value = "Wert / Value";
  worksheet.getCell(`C${row}`).value = "Ziel / Target";
  worksheet.getRow(row).font = { bold: true };
  worksheet.getRow(row).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFD0D0D0" },
  };
  row++;

  // Liquidity Ratios
  worksheet.getCell(`A${row}`).value = "LIQUIDITÄT / LIQUIDITY";
  worksheet.getCell(`A${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "Current Ratio";
  worksheet.getCell(`B${row}`).value = data.liquidity.currentRatio.value;
  worksheet.getCell(`B${row}`).numFmt = "0.00";
  worksheet.getCell(`C${row}`).value =
    data.liquidity.currentRatio.benchmark.target;
  worksheet.getCell(`C${row}`).numFmt = "0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Quick Ratio";
  worksheet.getCell(`B${row}`).value = data.liquidity.quickRatio.value;
  worksheet.getCell(`B${row}`).numFmt = "0.00";
  worksheet.getCell(`C${row}`).value =
    data.liquidity.quickRatio.benchmark.target;
  worksheet.getCell(`C${row}`).numFmt = "0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Cash Ratio";
  worksheet.getCell(`B${row}`).value = data.liquidity.cashRatio.value;
  worksheet.getCell(`B${row}`).numFmt = "0.00";
  worksheet.getCell(`C${row}`).value =
    data.liquidity.cashRatio.benchmark.target;
  worksheet.getCell(`C${row}`).numFmt = "0.00";
  row += 2;

  // Leverage Ratios
  worksheet.getCell(`A${row}`).value = "VERSCHULDUNG / LEVERAGE";
  worksheet.getCell(`A${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "Debt-to-Equity";
  worksheet.getCell(`B${row}`).value = data.leverage.debtToEquity.value;
  worksheet.getCell(`B${row}`).numFmt = "0.00";
  worksheet.getCell(`C${row}`).value =
    data.leverage.debtToEquity.benchmark.target;
  worksheet.getCell(`C${row}`).numFmt = "0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Debt Ratio";
  worksheet.getCell(`B${row}`).value = data.leverage.debtRatio.value;
  worksheet.getCell(`B${row}`).numFmt = "0.0%";
  worksheet.getCell(`C${row}`).value = data.leverage.debtRatio.benchmark.target;
  worksheet.getCell(`C${row}`).numFmt = "0.0%";
  row += 2;

  // Profitability Ratios
  worksheet.getCell(`A${row}`).value = "RENTABILITÄT / PROFITABILITY";
  worksheet.getCell(`A${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "Gross Margin";
  worksheet.getCell(`B${row}`).value =
    data.profitability.grossMargin.value / 100;
  worksheet.getCell(`B${row}`).numFmt = "0.0%";
  worksheet.getCell(`C${row}`).value =
    data.profitability.grossMargin.benchmark.target / 100;
  worksheet.getCell(`C${row}`).numFmt = "0.0%";
  row++;

  worksheet.getCell(`A${row}`).value = "Operating Margin";
  worksheet.getCell(`B${row}`).value =
    data.profitability.operatingMargin.value / 100;
  worksheet.getCell(`B${row}`).numFmt = "0.0%";
  worksheet.getCell(`C${row}`).value =
    data.profitability.operatingMargin.benchmark.target / 100;
  worksheet.getCell(`C${row}`).numFmt = "0.0%";
  row++;

  worksheet.getCell(`A${row}`).value = "Net Margin";
  worksheet.getCell(`B${row}`).value = data.profitability.netMargin.value / 100;
  worksheet.getCell(`B${row}`).numFmt = "0.0%";
  worksheet.getCell(`C${row}`).value =
    data.profitability.netMargin.benchmark.target / 100;
  worksheet.getCell(`C${row}`).numFmt = "0.0%";
  row++;

  worksheet.getCell(`A${row}`).value = "ROE (Return on Equity)";
  worksheet.getCell(`B${row}`).value = data.profitability.roe.value / 100;
  worksheet.getCell(`B${row}`).numFmt = "0.0%";
  worksheet.getCell(`C${row}`).value =
    data.profitability.roe.benchmark.target / 100;
  worksheet.getCell(`C${row}`).numFmt = "0.0%";
  row++;

  worksheet.getCell(`A${row}`).value = "ROA (Return on Assets)";
  worksheet.getCell(`B${row}`).value = data.profitability.roa.value / 100;
  worksheet.getCell(`B${row}`).numFmt = "0.0%";
  worksheet.getCell(`C${row}`).value =
    data.profitability.roa.benchmark.target / 100;
  worksheet.getCell(`C${row}`).numFmt = "0.0%";
  row += 2;

  // Efficiency Ratios
  worksheet.getCell(`A${row}`).value = "EFFIZIENZ / EFFICIENCY";
  worksheet.getCell(`A${row}`).font = { bold: true };
  row++;

  worksheet.getCell(`A${row}`).value = "Asset Turnover";
  worksheet.getCell(`B${row}`).value = data.efficiency.assetTurnover.value;
  worksheet.getCell(`B${row}`).numFmt = "0.00";
  worksheet.getCell(`C${row}`).value =
    data.efficiency.assetTurnover.benchmark.target;
  worksheet.getCell(`C${row}`).numFmt = "0.00";
  row++;

  worksheet.getCell(`A${row}`).value = "Days Receivables Outstanding";
  worksheet.getCell(`B${row}`).value =
    data.efficiency.daysReceivablesOutstanding.value;
  worksheet.getCell(`B${row}`).numFmt = "0";
  worksheet.getCell(`C${row}`).value =
    data.efficiency.daysReceivablesOutstanding.benchmark.target;
  worksheet.getCell(`C${row}`).numFmt = "0";
  row++;

  // Download
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `Kennzahlen_${companyName}_${new Date(data.period.endDate).toISOString().split("T")[0]}.xlsx`;
  link.click();
  URL.revokeObjectURL(url);
}
